import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Anchor, Divider, Loader } from '@mantine/core';
import NumberFormat from 'react-number-format';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import AppFlexbox from '../../common/AppFlexbox';
import { Context as HelperContext } from '../../../providers/HelperContextProvider';

const CheckoutOrderSummaryPriceBreakdown = ({ loading, regCart }) => {
  const currency = regCart?.cart.currency?.toUpperCase() ?? 'CAD';
  const checkoutCart = regCart?.cart;
  const orderSummaryLoading = loading || !regCart;
  const { toggleWebstoreChangeShippingModal } = useContext(HelperContext);

  return (
    <>
      <AppStack style={{ gap: 10, margin: 20 }}>
        <AppText style={{ fontSize: 18, fontWeight: 700 }}>
          Order Summary
        </AppText>
        <AppFlexbox
          style={{
            justifyContent: 'space-between',
            flexWrap: 'nowrap',
            alignItems: 'center'
          }}
        >
          <AppText style={{ fontSize: 16 }}>Subtotal</AppText>
          {orderSummaryLoading ? (
            <Loader color="dark" size={20} type="dots" />
          ) : (
            <AppFlexbox style={{ gap: 10 }}>
              <AppText style={{ fontSize: 16 }}>
                <NumberFormat
                  decimalScale={2}
                  displayType="text"
                  fixedDecimalScale
                  prefix="$"
                  thousandSeparator
                  value={checkoutCart.subtotalInCents / 100}
                />
              </AppText>
              <AppText style={{ fontSize: 16 }}>{currency}</AppText>
            </AppFlexbox>
          )}
        </AppFlexbox>
        {checkoutCart?.discountTotalInCents > 0 && (
          <AppFlexbox
            style={{
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
              alignItems: 'center'
            }}
          >
            <AppText style={{ fontSize: 16 }}>Discount</AppText>
            {orderSummaryLoading ? (
              <Loader color="dark" size={20} type="dots" />
            ) : (
              <AppFlexbox style={{ gap: 10 }}>
                <AppText style={{ fontSize: 16 }}>
                  <NumberFormat
                    decimalScale={2}
                    displayType="text"
                    fixedDecimalScale
                    prefix="$"
                    thousandSeparator
                    value={(checkoutCart.discountTotalInCents / 100) * -1}
                  />
                </AppText>
                <AppText style={{ fontSize: 16 }}>{currency}</AppText>
              </AppFlexbox>
            )}
          </AppFlexbox>
        )}

        {checkoutCart?.totalInCents > 0 &&
          checkoutCart.lineItems.some((i) => i.isPhysicalProduct) && (
            <AppFlexbox
              style={{
                justifyContent: 'space-between',
                flexWrap: 'nowrap'
              }}
            >
              <AppStack style={{ gap: 0 }}>
                <AppText style={{ fontSize: 16 }}>Shipping</AppText>
                {!orderSummaryLoading &&
                  checkoutCart.shippingCosts.length > 0 && (
                    <AppText style={{ color: '#666', fontSize: 13 }}>
                      ({checkoutCart.shippingCosts[0]?.label ?? 'Standard'})
                    </AppText>
                  )}
              </AppStack>

              {orderSummaryLoading ? (
                <Loader color="dark" size={20} type="dots" />
              ) : (
                <AppFlexbox style={{ gap: 10, alignItems: 'center' }}>
                  {checkoutCart.freeShipping ? (
                    <AppText style={{ fontSize: 18, color: 'green' }}>
                      Free
                    </AppText>
                  ) : !checkoutCart.shippingCostMissing ? (
                    <AppStack style={{ gap: 0, alignItems: 'end' }}>
                      <AppFlexbox style={{ gap: 10, alignItems: 'center' }}>
                        <AppText style={{ fontSize: 16 }}>
                          <NumberFormat
                            decimalScale={2}
                            displayType="text"
                            fixedDecimalScale
                            prefix="$"
                            thousandSeparator
                            value={checkoutCart.shippingTotalInCents / 100}
                          />
                        </AppText>
                        <AppText style={{ fontSize: 16 }}>{currency}</AppText>
                      </AppFlexbox>

                      {!orderSummaryLoading && (
                        <Anchor
                          onClick={() => {
                            toggleWebstoreChangeShippingModal({ isOpen: true });
                          }}
                          style={{ color: 'dodgerblue', fontSize: 13 }}
                        >
                          Change shipping
                        </Anchor>
                      )}
                    </AppStack>
                  ) : (
                    <AppText style={{ color: '#666', fontSize: 13 }}>
                      Enter shipping address
                    </AppText>
                  )}
                </AppFlexbox>
              )}
            </AppFlexbox>
          )}

        <AppFlexbox
          style={{
            justifyContent: 'space-between',
            flexWrap: 'nowrap',
            alignItems: 'center'
          }}
        >
          <AppText style={{ fontSize: 16 }}>Taxes</AppText>
          {orderSummaryLoading ? (
            <Loader color="dark" size={20} type="dots" />
          ) : (
            <AppFlexbox style={{ gap: 10 }}>
              <AppText style={{ fontSize: 16 }}>
                <NumberFormat
                  decimalScale={2}
                  displayType="text"
                  fixedDecimalScale
                  prefix="$"
                  thousandSeparator
                  value={checkoutCart.taxTotalInCents / 100}
                />
              </AppText>
              <AppText style={{ fontSize: 16 }}>{currency}</AppText>
            </AppFlexbox>
          )}
        </AppFlexbox>

        {checkoutCart?.customerServiceFeeTotalInCents > 0 && (
          <AppFlexbox
            style={{
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
              alignItems: 'center'
            }}
          >
            <AppText style={{ fontSize: 16 }}>Service Fee</AppText>
            {orderSummaryLoading ? (
              <Loader color="dark" size={20} type="dots" />
            ) : (
              <AppFlexbox style={{ gap: 10 }}>
                <AppText style={{ fontSize: 16 }}>
                  <NumberFormat
                    decimalScale={2}
                    displayType="text"
                    fixedDecimalScale
                    prefix="$"
                    thousandSeparator
                    value={checkoutCart.customerServiceFeeTotalInCents / 100}
                  />
                </AppText>
                <AppText style={{ fontSize: 16 }}>{currency}</AppText>
              </AppFlexbox>
            )}
          </AppFlexbox>
        )}
        {checkoutCart?.tieredPricingFeeInCents > 0 && (
          <AppFlexbox
            style={{
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
              alignItems: 'center'
            }}
          >
            <AppText style={{ fontSize: 16 }}>Platform Fee</AppText>
            {orderSummaryLoading ? (
              <Loader color="dark" size={20} type="dots" />
            ) : (
              <AppFlexbox style={{ gap: 10 }}>
                <AppText style={{ fontSize: 16 }}>
                  <NumberFormat
                    decimalScale={2}
                    displayType="text"
                    fixedDecimalScale
                    prefix="$"
                    thousandSeparator
                    value={checkoutCart.tieredPricingFeeInCents / 100}
                  />
                </AppText>
                <AppText style={{ fontSize: 16 }}>{currency}</AppText>
              </AppFlexbox>
            )}
          </AppFlexbox>
        )}

        {checkoutCart?.checkoutTotalInCents !== checkoutCart?.totalInCents && (
          <AppFlexbox
            style={{
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
              alignItems: 'center'
            }}
          >
            <AppText style={{ fontSize: 16 }}>Assistance</AppText>
            {orderSummaryLoading ? (
              <Loader color="dark" size={20} type="dots" />
            ) : (
              <AppFlexbox style={{ gap: 10 }}>
                <AppText style={{ fontSize: 16 }}>
                  <NumberFormat
                    decimalScale={2}
                    displayType="text"
                    fixedDecimalScale
                    prefix="$"
                    thousandSeparator
                    value={
                      (checkoutCart.checkoutTotalInCents -
                        checkoutCart.totalInCents) /
                      100
                    }
                  />
                </AppText>
                <AppText style={{ fontSize: 16 }}>{currency}</AppText>
              </AppFlexbox>
            )}
          </AppFlexbox>
        )}
      </AppStack>
      <Divider />
      <AppFlexbox
        style={{
          justifyContent: 'space-between',
          flexWrap: 'nowrap',
          margin: 20
        }}
      >
        <AppText size="lg" weight={700}>
          Total
        </AppText>
        {orderSummaryLoading ? (
          <Loader color="dark" size={20} type="dots" />
        ) : (
          <AppFlexbox style={{ gap: 10 }}>
            <AppText size="lg" weight={700}>
              <NumberFormat
                decimalScale={2}
                displayType="text"
                fixedDecimalScale
                prefix="$"
                thousandSeparator
                value={checkoutCart.checkoutTotalInCents / 100}
              />
            </AppText>
            <AppText size="lg" weight={700}>
              {currency}
            </AppText>
          </AppFlexbox>
        )}
      </AppFlexbox>
    </>
  );
};

CheckoutOrderSummaryPriceBreakdown.propTypes = {
  loading: PropTypes.bool,
  regCart: PropTypes.object
};

export default CheckoutOrderSummaryPriceBreakdown;
